import type { ComponentTypes, EditableStateInfo, StorySettingsOfCard } from 'types/story';
import type { SCFontItem } from 'types/fonts';
import { templateString } from 'utils/template-string';

export { IS_DEVEL } from 'utils/environment';
export { DEFAULT_MEDIA_QUERY_PLATFORMS, MQ_SIZES_TEXT_MAP, DEFAULT_MEDIA_QUERY_SIZES } from './story-media-query';
export { PARAMS, SEARCH_PARAMS, CARD_PATH_SEGMENT } from './url-params';
export const ADMIN_SESSION = 'adminSessionId';
export const CLIENT_SESSION = 'sessionId';

export const CARD_TYPE = {
	INFO: 'INFO',
	TRIVIA: 'TRIVIA',
	SORTABLE_TRIVIA: 'SORTABLE_TRIVIA',
	SORTABLE_POLL: 'SORTABLE_POLL',
	POLL: 'POLL',
	PERSONALITY_TEST: 'PERSONALITY_TEST',
	TRUE_OR_FALSE: 'TRUE_OR_FALSE',
	THIS_OR_THAT: 'THIS_OR_THAT',
	NAVIGATION: 'NAVIGATION',
	FORM: 'FORM',
	REGISTRATION: 'REGISTRATION',
	SANDBOX: 'SANDBOX',
} as const;

export const COMPONENT_TYPE = {
	TEXT: 'TEXT',
	IMG: 'IMG',
	ICON: 'ICON',
	ANSWER: 'ANSWER',
	ANSWER_PROGRESS: 'ANSWER_PROGRESS',
	AD_UNIT: 'AD_UNIT',
	FLOAT_ABOVE: 'FLOAT_ABOVE',
	FLOAT_BELOW: 'FLOAT_BELOW',
	CONTENT: 'CONTENT',
	CARD: 'CARD',
	BUTTON: 'BUTTON',
	BUTTON_SUBMIT: 'BUTTON_SUBMIT',
	GOOGLE_LOGIN: 'GOOGLE_LOGIN',
	SMS_LOGIN: 'SMS_LOGIN',
	BOX: 'BOX',
	SHAPE: 'SHAPE',
	EMBED: 'EMBED',
	TEXT_FIELD: 'TEXT_FIELD',
	TEXT_AREA_FIELD: 'TEXT_AREA_FIELD',
	CHECKBOX: 'CHECKBOX',
	SHARE: 'SHARE',
	RESULT_TEXT: 'RESULT_TEXT',
	RESULT_SHAPE: 'RESULT_SHAPE',
	SWIPE: 'SWIPE',
	VIDEO: 'VIDEO',
	TIMER: 'TIMER',
	LOTTIE: 'LOTTIE',
	SELECT: 'SELECT',
	OVERLAY: 'OVERLAY',
	SORTABLE_BOX: 'SORTABLE_BOX',
	COUNTER: 'COUNTER',
	ANSWER_INPUT: 'ANSWER_INPUT',
	SLIDER: 'SLIDER',
	SLIDER_SLIDES: 'SLIDER_SLIDES',
	SLIDER_ARROW: 'SLIDER_ARROW',
	SLIDER_PAGINATION: 'SLIDER_PAGINATION',
	VISUAL_COMPARE: 'VISUAL_COMPARE',
	SEARCH_FIELD: 'SEARCH_FIELD',
	CMS_REPEATER: 'CMS_REPEATER',
	FILE_UPLOAD_FIELD: 'FILE_UPLOAD_FIELD',
} as const;

export const COMPONENT_DISPLAY_NAME: Record<keyof typeof COMPONENT_TYPE, string> = {
	TEXT: 'Text',
	IMG: 'Image',
	ICON: 'Icon',
	ANSWER: 'Answer',
	AD_UNIT: 'Ad Slot',
	ANSWER_PROGRESS: 'AnswerProgress',
	FLOAT_ABOVE: 'Float above',
	FLOAT_BELOW: 'Float below',
	CONTENT: 'Content',
	CARD: 'Card',
	BUTTON: 'Button',
	BUTTON_SUBMIT: 'Submit',
	GOOGLE_LOGIN: 'Google login',
	SMS_LOGIN: 'SMS login',
	BOX: 'Box',
	SHAPE: 'Shape',
	EMBED: 'Embed',
	TEXT_FIELD: 'TextField',
	TEXT_AREA_FIELD: 'TextAreaField',
	CHECKBOX: 'Checkbox',
	SHARE: 'Share',
	RESULT_TEXT: 'ResultText',
	RESULT_SHAPE: 'ResultShape',
	SWIPE: 'Swipe',
	VIDEO: 'Media',
	TIMER: 'Timer',
	LOTTIE: 'Lottie',
	SELECT: 'Select',
	OVERLAY: 'Overlay',
	SORTABLE_BOX: 'SortableBox',
	COUNTER: 'Counter',
	ANSWER_INPUT: 'AnswerInput',
	SLIDER: 'Slider',
	SLIDER_ARROW: 'Arrow',
	SLIDER_PAGINATION: 'Pagination',
	SLIDER_SLIDES: 'Slides container',
	VISUAL_COMPARE: 'Photo compare',
	SEARCH_FIELD: 'Search field',
	CMS_REPEATER: 'CMS Repeater',
	FILE_UPLOAD_FIELD: 'File Upload',
};

export const STORY_SETTINGS_COMPONENTS = {
	[COMPONENT_TYPE.CARD]: {
		path: templateString('cards.{{ cardId }}'),
	},
	[COMPONENT_TYPE.ANSWER]: {
		path: templateString('cards.{{ cardId }}.answers.{{ answerId }}'),
	},
};

export const CARD_ROOT_ELEMENT_INDEX = {
	[COMPONENT_TYPE.FLOAT_ABOVE]: 0,
	[COMPONENT_TYPE.CONTENT]: 1,
	[COMPONENT_TYPE.FLOAT_BELOW]: 2,
	[COMPONENT_TYPE.CARD]: 3,
} as const;

export const STORY_ROOT_ELEMENT_INDEX = {
	[COMPONENT_TYPE.FLOAT_ABOVE]: 0,
	[COMPONENT_TYPE.FLOAT_BELOW]: 1,
};

export const LAYER_TYPE = {
	// this layer adds block to story.elements and leave his original ref in card element without render.
	// Also wraps it with a fixed positioned container
	FIXED_GLOBAL: 0,
	FIXED: 1, // this layer wraps block with a fixed positioned container
	FLOAT: 2, // this layer moves block to one of the float layers
	CONTENT: undefined, // this layer moves block to "content" container at card elements
} as const;

export const IFRAME_ACTIONS = {
	GET_STORY: '@IFRAME/GET_STORY',
	UPDATE_ADMIN_PREVIEW_LOCATION: '@IFRAME/UPDATE_ADMIN_LOCATION',
	UPDATE_CARD_ELEMENT_BY_PATH: '@IFRAME/UPDATE_CARD_ELEMENT_BY_PATH',
	UPDATE_STORY_ELEMENT_BY_PATH: '@IFRAME/UPDATE_STORY_ELEMENT_BY_PATH',
	SET_EDITABLE_EDITOR_ELEMENT: '@IFRAME/SET_EDITABLE_EDITOR_ELEMENT',
	INITIATE_SELECT_EDITOR_ELEMENT: '@IFRAME/INITIATE_SELECT_EDITOR_ELEMENT',
	APPLY_SCROLLBAR_FIX: '@IFRAME/APPLY_SCROLLBAR_FIX',
	UPDATE_CLIENT_CARD_DATA: '@EDITOR/UPDATE_CLIENT_CARD_DATA',
	UPDATE_CLIENT_STORY_ELEMENTS: '@EDITOR/UPDATE_CLIENT_STORY_ELEMENTS',
};

// Supported states by app
export const COMPONENT_STATES = {
	DEFAULT: 'defaultState',
	// [data-selected-state]
	SELECTED: 'selectedState',
	// [data-hover-state]
	HOVER: 'hoverState',
	// [data-selected-hover-state]
	SELECTED_HOVER: 'selectedHoverState',
	// [data-correct-state]
	CORRECT: 'correctState',
	// [data-incorrect-state]
	INCORRECT: 'incorrectState',
	// [data-empty-state]
	EMPTY: 'emptyState',
	// [data-disabled-state]
	DISABLED: 'disabledState',
} as const;

// List of states in order of assignment
export const COMPONENT_STATES_ORDERED = [
	COMPONENT_STATES.DEFAULT,
	COMPONENT_STATES.SELECTED,
	COMPONENT_STATES.HOVER,
	COMPONENT_STATES.SELECTED_HOVER,
	COMPONENT_STATES.CORRECT,
	COMPONENT_STATES.INCORRECT,
	COMPONENT_STATES.DISABLED,
	COMPONENT_STATES.EMPTY,
];

const NON_HOVERABLE_COMPONENT_TYPES = new Set<ComponentTypes>([
	COMPONENT_TYPE.FLOAT_ABOVE,
	COMPONENT_TYPE.FLOAT_BELOW,
	COMPONENT_TYPE.CONTENT,
	COMPONENT_TYPE.CARD,
]);

const DEFAULT_HOVER_COMPONENT_STATES = [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.HOVER];

// Hover is local to every ordinary building block. Card roots are structural render scopes, not authored blocks.
const HOVERABLE_COMPONENT_STATES_LIST = Object.fromEntries(
	Object.values(COMPONENT_TYPE)
		.filter(type => !NON_HOVERABLE_COMPONENT_TYPES.has(type))
		.map(type => [type, DEFAULT_HOVER_COMPONENT_STATES])
);

// List of states used by particular building blocks
export const COMPONENT_STATES_LIST = {
	...HOVERABLE_COMPONENT_STATES_LIST,
	[COMPONENT_TYPE.ANSWER]: [
		COMPONENT_STATES.DEFAULT,
		COMPONENT_STATES.SELECTED,
		COMPONENT_STATES.CORRECT,
		COMPONENT_STATES.INCORRECT,
		COMPONENT_STATES.HOVER,
	],
	[COMPONENT_TYPE.ANSWER_INPUT]: [
		COMPONENT_STATES.DEFAULT,
		COMPONENT_STATES.HOVER,
		COMPONENT_STATES.CORRECT,
		COMPONENT_STATES.INCORRECT,
		COMPONENT_STATES.DISABLED,
	],
	[COMPONENT_TYPE.TEXT_FIELD]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.HOVER, COMPONENT_STATES.INCORRECT],
	[COMPONENT_TYPE.TEXT_AREA_FIELD]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.HOVER, COMPONENT_STATES.INCORRECT],
	[COMPONENT_TYPE.CHECKBOX]: [
		COMPONENT_STATES.DEFAULT,
		COMPONENT_STATES.HOVER,
		COMPONENT_STATES.SELECTED,
		COMPONENT_STATES.INCORRECT,
	],
	[COMPONENT_TYPE.SELECT]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.HOVER, COMPONENT_STATES.INCORRECT],
	[COMPONENT_TYPE.FILE_UPLOAD_FIELD]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.HOVER, COMPONENT_STATES.INCORRECT],
	[COMPONENT_TYPE.SWIPE]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.HOVER, COMPONENT_STATES.SELECTED],
	[COMPONENT_TYPE.CMS_REPEATER]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.HOVER, COMPONENT_STATES.EMPTY],
};

export const SCORE = {
	DEFAULT: 5, // 5 is a default value at backend,
	MIN: 0, // total score min
	MAX: 100, // total score max
	RANGE: {
		MIN: 0, // total score min
		MAX: 100, // total score max
		MIN_RANGE_SIZE: 10, // min range size
	},
};

export const ANSWER_INPUT_SUBMISSION_ATTEMPTS = {
	DEFAULT: 1,
	MIN: 1,
	MAX: 100,
};

export const NUM_SELECTED_ANSWERS = {
	MIN: 1,
	MAX: 30,
};

export const FLOW_DEFAULT_EVENT_NAME = 'Default';
export const FLOW_ANSWER_CORRECT_EVENT_NAME = 'Correct Answer';
export const FLOW_ANSWER_INCORRECT_EVENT_NAME = 'Incorrect Answer';

// Flow event connection type
export const CONNECTION_TYPES = {
	NEXT: 'NEXT', // bind to 1st card in next step
	ANY: 'ANY', // bind to point by end point id (card)
	STEP: 'STEP', // bind to point by end point id (step) (random card in step)
} as const;

export enum Pin {
	l = 'l',
	t = 't',
	r = 'r',
	b = 'b',
	c = 'c',
	lt = 'lt',
	lb = 'lb',
	rb = 'rb',
	rt = 'rt',
	none = '',
}
export const PIN_DEFAULT = Pin.t;

export const PIN_ATTR = 'data-pin';

export const IMG_TINT_ATTR = 'data-img-tint';

export const CONTENT_ALIGN_Y = {
	top: 'top',
	center: 'center',
	bottom: 'bottom',
} as const;

export const TRANSITION_TYPE = {
	NONE: 'none',
	FADE: 'fade',
	ZOOM: 'zoom',
} as const;

export const EXPOSE_DEFAULT_DURATION = 0; // seconds

export enum ANIMATION_EFFECT_ID {
	BG_COLOR = 'BG_COLOR',
	COLOR = 'COLOR',
	OPACITY = 'OPACITY',
	X = 'X',
	Y = 'Y',
	ROTATE = 'ROTATE',
	SCALE = 'SCALE',
	SCALE_X = 'SCALE_X',
	SCALE_Y = 'SCALE_Y',
}

export const ANIMATION_STATE = {
	NONE: 'NONE',
	NOT_STARTED: 'NOT_STARTED',
	PLAYING: 'PLAYING',
	COMPLETE: 'COMPLETE',
} as const;

// Animation triggers that are handled by EvenEmitter (see `const AnimationEvEm`)
const ANIMATION_EVENT_EMITTER_TRIGGER = {
	ON_CARD_ANSWER: 'onCardAnswer',
	ON_CARD_ANSWER_CORRECT: 'onCardAnswerCorrect',
	ON_CARD_ANSWER_INCORRECT: 'onCardAnswerIncorrect',
	ON_SUBMIT: 'onSubmit',
};

// List of all available animation triggers
export const ANIMATION_TRIGGER = {
	ON_LOAD: 'onLoad',
	ON_CARD_EXPOSE_START: 'onCardExposeStart',
	ON_CARD_EXPOSE_COMPLETE: 'onCardExposeComplete',
	ON_VIEWPORT: 'onViewport',
	ON_HOVER: 'onMouseEnter',
	ON_CLICK: 'onClick',
	...ANIMATION_EVENT_EMITTER_TRIGGER,
} as const;

export const ANIMATION_KEYFRAMES = {
	INITIAL: 'initial',
	FINAL: 'final',
} as const;

export const DEFAULT_EDITABLE_STATE_INFO: EditableStateInfo = {
	state: COMPONENT_STATES.DEFAULT,
	source: {
		id: '',
		path: '',
		type: '',
		layer: LAYER_TYPE.CONTENT,
	},
} as const;

export const DISABLED_ANSWER_ATTR = 'data-disabled-answer';

type CardSettingsKeys = keyof StorySettingsOfCard;
interface StorySettingsPropI {
	showResults: Extract<CardSettingsKeys, 'showResults'>;
	showCorrect: Extract<CardSettingsKeys, 'showCorrect'>;
	score: Extract<CardSettingsKeys, 'score'>;
	maxAttempts: Extract<CardSettingsKeys, 'maxAttempts'>;
	countType: Extract<CardSettingsKeys, 'countType'>;
	answersMinMax: Extract<CardSettingsKeys, 'answersMinMax'>;
	forceSubmitBtn: Extract<CardSettingsKeys, 'forceSubmitBtn'>;
	sortableType: Extract<CardSettingsKeys, 'sortableType'>;
}
export const STORY_SETTING_PROP: StorySettingsPropI = {
	showResults: 'showResults',
	showCorrect: 'showCorrect',
	score: 'score',
	maxAttempts: 'maxAttempts',
	countType: 'countType',
	answersMinMax: 'answersMinMax',
	forceSubmitBtn: 'forceSubmitBtn',
	sortableType: 'sortableType',
};

// alias for "StorySettingsType.cards.<cardId>.answers.<answerId>.characterPoints.<characterId>: number"
export const characterPoints = 'characterPoints';

export const ELEMENT_EDIT_MODE = {
	INSTANCE: 'INSTANCE', // edit current element (default)
	MASTER: 'MASTER', // edit master symbol linked to current element (Symbols only)
} as const;

export const CARD_DEFAULT_TEMPLATE_NAME = 'DEFAULT';

export const STORY_TYPE = {
	STANDALONE: 'standalone',
	EMBED: 'embed',
	WIDGET: 'widget',
} as const;

export const SYSTEM_FONTS: Record<string, Pick<SCFontItem, 'url' | 'fontType' | 'fontFamily'>> = {
	VARELA_ROUND: {
		fontFamily: '"Varela Round", sans-serif',
		fontType: 'google',
		url: 'Varela+Round',
	},
	MIRIAM_LIBRE: {
		fontFamily: '"Miriam Libre", sans-serif',
		fontType: 'google',
		url: 'Miriam+Libre:wght@400;700',
	},
	SUEZ_ONE: {
		fontFamily: '"Suez One", serif',
		fontType: 'google',
		url: 'Suez+One',
	},
	SECULAR_ONE: {
		fontFamily: '"Secular One", sans-serif',
		fontType: 'google',
		url: 'Secular+One',
	},
	ALEF: {
		fontFamily: 'Alef, sans-serif',
		fontType: 'google',
		url: 'Alef:wght@400;700',
	},
	RUBIK: {
		fontFamily: 'Rubik, sans-serif',
		fontType: 'google',
		url: 'Rubik:ital,wght@0,300;0,400;0,500;0,700;0,900;1,300;1,400;1,500;1,700;1,900',
	},
};

export const GPT_AD_DEFAULT_CONTAINER_ID = 'gpt-ad-container';

export const GPT_AD_DEFAULT_CLOSING_FN = 'onStoryAdvClose';

export const AI_TEXT_ADJUSTMENT_OPTIONS = [
	{ value: '', label: 'Neutral' },
	// Often used in business, academic, or professional contexts where a formal and respectful tone is required
	{ value: 'Make it more formal.', label: 'Formal' },
	// Useful for making complex or technical information accessible to a general audience or for ensuring clarity.
	{ value: 'Make it simpler and clearer.', label: 'Friendly' },
	// Desired in marketing or social media to make the content more engaging and relatable to the audience
	{ value: 'Add a humorous touch', label: 'Humor' },
	// Important in sales, advertising, or argumentative writing where the goal is to convince or influence the reader.
	{ value: 'Give it a persuasive edge.', label: 'Persuasive' },
	// Used in industry-specific writing, where a high level of professionalism and use of jargon is expected.
	{ value: 'Make it sound professional and technical.', label: 'Intellectual' },
];

export const STORY_VERSIONS = {
	latest: 'latest',
	published: 'published',
	archived: 'archived',
	unpublished: 'unpublished',
} as const;

// Navigation card count types
export enum CountType {
	score = 'score', // Total score calculated relative to the maximum
	character = 'character', // Total number of points scored by an individual character
	ab = 'ab', // A/B navigation
	urlParam = 'urlParam', // URL parameter
	redirect = 'redirect', // Redirect
	gptTextResponse = 'gptTextResponse', // GPT Text Response
	geolocation = 'geolocation', // Geolocation
}

export const LINK_ELEMENT_TRACKER_OPTIONS = { LINK_URL: { value: '$LINK_URL', label: 'Link URL' } } as const;
